import { searchForHits } from '@iheartjane/algolia';
import type { SearchProps, SearchResponse } from '@iheartjane/algolia';
import { config } from '@iheartjane/config';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { ZodTypeAny } from 'zod';

export function useSearch<T>({ enabled = true, ...params }: SearchProps<T>) {
  return useQuery({
    enabled,
    queryFn: () => search<T>(params),
    queryKey: ['searchResponse', JSON.stringify(params)],
  });
}

export function useInfiniteSearch<T>({
  enabled = true,
  ...params
}: SearchProps<T>) {
  return useInfiniteQuery({
    enabled,
    getNextPageParam: (lastPage) => {
      const maybeNextPage = (lastPage?.page ?? 0) + 1;
      const hasNextPage = maybeNextPage < (lastPage?.nbPages ?? 0);
      return hasNextPage ? maybeNextPage : undefined;
    },
    onError: params.onError,
    queryFn: ({ pageParam }: { pageParam?: number }) =>
      search<T>({ ...params, page: pageParam }),
    queryKey: ['infiniteSearchResponse', JSON.stringify(params)],
  });
}

/** @private */
export async function search<T>(
  args: SearchProps<T>,
  schema?: ZodTypeAny
): Promise<SearchResponse<T>> {
  const response = await searchForHits(
    {
      algoliaEnv: config.algoliaEnv,
      apiKey: config.algoliaApiKey,
      appId: config.algoliaAppId,
      url: config.algoliaUrl,
    },
    args
  );

  if (schema) {
    const { hits, ...rest } = response;
    const parsed = await schema.safeParseAsync(hits);

    if (!parsed.success) {
      const error = parsed.error;
      error.issues.forEach((e) => console.error(e));
    }

    return {
      hits: parsed.success ? parsed.data : hits,
      ...rest,
    };
  }

  return response;
}
